import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { useToast } from '@/components/ui';
import { PHONE_QUERY, useMediaQuery } from '@/lib/hooks';

// Submodule path, as in `bellItems.ts`: the payments barrel carries every payment screen.
import {
  useBoletoNoticesQuery,
  useDeleteBoletoNoticeMutation,
  useDismissBoletoNoticeMutation,
} from '@/features/payments/boletoNotices';

import {
  fromBoleto,
  fromInactivity,
  itemToFloat,
  mergeBellItems,
  type BellItem,
} from './bellItems';
import { NoticeCard } from './NoticeCard';
import {
  useDeleteNoticesMutation,
  useDismissNoticeMutation,
  useInactivityAudience,
  useInactivityNoticesQuery,
  useRequestReactivationMutation,
} from './queries';
import styles from './NotificationBell.module.css';

/** I9: the bell opens on a resting pointer after this long, so passing over it does nothing. */
const HOVER_OPEN_DELAY_MS = 150;
/** Long enough to cross the 8px gap between the bell and its list without it closing. */
const HOVER_CLOSE_DELAY_MS = 200;

const FINE_POINTER = '(hover: hover) and (pointer: fine)';

/**
 * The bell in the top bar, and the warning that floats under it on entry (Figma I8, I9).
 *
 * The bell lists the notices that are still current — the inactivity warnings, blocks and
 * reactivations, and the boleto reminders, from the last 30 days, nothing a later notice replaced,
 * nothing this person deleted — with a × on each that deletes it and "Excluir todas". Each source
 * keeps its own API; they meet here, as `BellItem`s. It opens on a resting pointer (150ms), on a click or a tap, and closes on Esc, a click
 * outside or the pointer leaving; on a phone the same list is a sheet from the bottom edge. There is
 * no notifications page, on purpose: the list is short by construction.
 *
 * On entry, the most urgent notice not closed today floats under the bell, sliding 12px down as it
 * fades in (260ms, out-strong). It never hides by itself; its × closes it for today only — the
 * notice stays in the bell — and its action closes it too.
 *
 * Drawn only for the profiles the rule speaks to (`useInactivityAudience`), like the cart pill.
 */
export interface NotificationBellProps {
  /** `on-brand`: white on the red header of the redesign (Figma D1), with a white count. */
  readonly tone?: 'default' | 'on-brand' | undefined;
}

export function NotificationBell({ tone = 'default' }: NotificationBellProps = {}) {
  const audience = useInactivityAudience();
  if (!audience) {
    return null;
  }
  return <Bell tone={tone} />;
}

function Bell({ tone }: { readonly tone: 'default' | 'on-brand' }) {
  const inactivity = useInactivityNoticesQuery();
  const boletos = useBoletoNoticesQuery();
  const dismissInactivity = useDismissNoticeMutation();
  const removeInactivity = useDeleteNoticesMutation();
  const dismissBoleto = useDismissBoletoNoticeMutation();
  const removeBoleto = useDeleteBoletoNoticeMutation();
  const requestReactivation = useRequestReactivationMutation();
  const navigate = useNavigate();
  const toast = useToast();
  const phone = useMediaQuery(PHONE_QUERY);
  const finePointer = useMediaQuery(FINE_POINTER);

  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const hoverTimer = useRef<number | undefined>(undefined);
  // Removed here at once, before either API answers, so the × never feels slow.
  const [gone, setGone] = useState<ReadonlySet<string>>(() => new Set());

  // A source that failed to load lends nothing to the list rather than hiding the other one.
  const items = mergeBellItems(
    (inactivity.data ?? []).map(fromInactivity),
    (boletos.data ?? []).map(fromBoleto),
  ).filter((item) => !gone.has(item.key));
  const count = items.length;
  const settled = !inactivity.isPending && (!boletos.isPending || boletos.isError);

  // The floating warning is chosen once, when the lists first arrive: it belongs to "entering",
  // and a notice arriving later in the session goes to the bell's count rather than over the page.
  const [floatKey, setFloatKey] = useState<string | null | undefined>(undefined);
  if (floatKey === undefined && settled) {
    setFloatKey(itemToFloat(items)?.key ?? null);
  }
  const floating = items.find((item) => item.key === floatKey);

  const clearHover = () => {
    window.clearTimeout(hoverTimer.current);
  };

  const close = useCallback(() => {
    window.clearTimeout(hoverTimer.current);
    setOpen(false);
  }, []);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: MouseEvent) => {
      if (rootRef.current !== null && !rootRef.current.contains(event.target as Node)) {
        close();
      }
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') close();
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open, close]);

  useEffect(() => () => window.clearTimeout(hoverTimer.current), []);

  const closeFloat = (item: BellItem) => {
    setFloatKey(null);
    if (item.source === 'inactivity') dismissInactivity.mutate(item.id);
    else dismissBoleto.mutate(item.id);
  };

  const remove = (item: BellItem) => {
    if (item.key === floatKey) setFloatKey(null);
    setGone((current) => new Set(current).add(item.key));
    if (item.source === 'inactivity') removeInactivity.mutate(item.id);
    else removeBoleto.mutate(item.id);
  };

  const removeAll = () => {
    setFloatKey(null);
    setGone(new Set(items.map((item) => item.key)));
    removeInactivity.mutate(null);
    removeBoleto.mutate(null);
  };

  const act = (item: BellItem, from: 'float' | 'list') => {
    if (from === 'float') {
      closeFloat(item);
    } else {
      close();
    }
    const action = item.action;
    if (action === null) return;
    if (action.kind === 'request_reactivation') {
      requestReactivation.mutate(
        { establishmentId: action.establishmentId },
        {
          onSuccess: (result) => {
            toast.success(
              result.created ? 'Pedido de reativação enviado' : 'A reativação já foi pedida',
              'A equipe da Instoque libera normalmente no mesmo dia útil.',
            );
          },
          onError: () => {
            toast.error('Não foi possível pedir a reativação');
          },
        },
      );
      return;
    }
    void navigate(action.href, { viewTransition: true });
  };

  const hoverProps = finePointer
    ? {
        onMouseEnter: () => {
          clearHover();
          if (!open) {
            hoverTimer.current = window.setTimeout(() => {
              setOpen(true);
            }, HOVER_OPEN_DELAY_MS);
          }
        },
        onMouseLeave: () => {
          clearHover();
          hoverTimer.current = window.setTimeout(close, HOVER_CLOSE_DELAY_MS);
        },
      }
    : {};

  const label =
    count > 0
      ? `Notificações, ${String(count)} ${count === 1 ? 'aviso' : 'avisos'}`
      : 'Notificações';

  return (
    <div
      className={styles.root}
      ref={rootRef}
      data-tone={tone === 'on-brand' ? 'on-brand' : undefined}
      {...hoverProps}
    >
      <button
        type="button"
        className={styles.bell}
        aria-label={label}
        aria-haspopup="dialog"
        aria-expanded={open}
        data-open={open ? '' : undefined}
        onClick={() => {
          clearHover();
          setOpen((value) => !value);
        }}
      >
        <svg
          className={styles.icon}
          viewBox="0 0 24 24"
          aria-hidden="true"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M6 9a6 6 0 1 1 12 0c0 4.5 1.5 6.5 2.5 7.5h-17C4.5 15.5 6 13.5 6 9Z" />
          <path d="M10 19.5a2 2 0 0 0 4 0" />
        </svg>
        {count > 0 ? (
          <span className={styles.count} aria-hidden="true">
            {count > 9 ? '9+' : count}
          </span>
        ) : null}
      </button>

      {open ? (
        <>
          {phone ? (
            <button
              type="button"
              className={styles.scrim}
              aria-label="Fechar notificações"
              onClick={close}
            />
          ) : null}
          <div
            className={styles.panel}
            data-sheet={phone ? '' : undefined}
            role="dialog"
            aria-label="Notificações"
          >
            <div className={styles.panelHeader}>
              <p className={styles.panelTitle}>
                Notificações{count > 0 ? ` · ${String(count)}` : ''}
              </p>
              {count > 0 ? (
                <button type="button" className={styles.deleteAll} onClick={removeAll}>
                  Excluir todas
                </button>
              ) : null}
              {tone === 'on-brand' ? (
                <Link
                  className={styles.deleteAll}
                  to="/notificacoes"
                  onClick={close}
                  viewTransition
                >
                  Ver todas
                </Link>
              ) : null}
            </div>
            {count === 0 && inactivity.isError && boletos.isError ? (
              <p className={styles.empty}>Não foi possível carregar as notificações.</p>
            ) : count === 0 ? (
              <p className={styles.empty}>Nenhuma notificação</p>
            ) : (
              <ul className={styles.list}>
                {items.map((item) => (
                  <li key={item.key}>
                    <NoticeCard
                      item={item}
                      variant="list"
                      closeLabel="Excluir"
                      actionPending={
                        item.action?.kind === 'request_reactivation' &&
                        requestReactivation.isPending &&
                        requestReactivation.variables.establishmentId ===
                          item.action.establishmentId
                      }
                      onAction={(chosen) => {
                        act(chosen, 'list');
                      }}
                      onClose={remove}
                    />
                  </li>
                ))}
              </ul>
            )}
          </div>
        </>
      ) : null}

      {floating !== undefined && !open ? (
        <div className={styles.float} role="status" aria-label="Aviso de inatividade">
          <NoticeCard
            item={floating}
            variant="float"
            closeLabel="Fechar aviso"
            onAction={(item) => {
              act(item, 'float');
            }}
            onClose={closeFloat}
          />
        </div>
      ) : null}
    </div>
  );
}
